/* eslint-disable max-len */
import * as Icons from 'icons';
import React from 'react';
import { Button, ButtonGroup, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import LindaImage from 'assets/images/linda.jpg';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { MaskedSwedishSsnInput } from '../../../input/components';
import { MaskedSwedishPostNumberInput } from '../../../input/components/masked-swedish-postnumber-input';
import { Form } from '../../../form';
import { FormSectionInfo } from '../../../form/components';
import { RHFInputText } from '../../../../packages/react-hook-form';
import { FormSectionContainer } from '../../../form/components/form-section-container/form-section-container';
import { BuyProcessFormUserTip } from '../user-tip/user-tip';
import { BuyProcessUtils } from '../../index';
import { useAuthContext } from '../../../app/components/auth/auth';
import { PromoPanelTitle } from '../../../promo-panel/promo-panel-title';
import { PromoPanelRow } from '../../../promo-panel/promo-panel-row';
import { PromoPanelContainer } from '../../../promo-panel/promo-panel-container';
import {
  CUSTOMER_FORM_ID,
  SWEDISH_SSN_PLACEHOLDER,
} from '../../../../global/constants';
import { BuyProcessFormNextButton } from '../buy-process-form-next-button/buy-process-form-next-button';
import { BuyProcessStepsTestIds } from '../../../../utils/testing/buy-process-steps-test-ids';
import { BuyProcessFormAdminSubmitButton } from '../buy-process-form-admin-submit-button/buy-process-form-admin-submit-button';
import { useClientInfo } from './use-client-info';

const useStyles = makeStyles((theme) => ({
  fieldsContainer: {
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(2),
    },
  },
  formIntroTitle: {
    '& > h4': {
      fontSize: '1.7rem',
    },
  },

  layoutContainer: {
    display: 'flex',
  },
  form: {
    flexShrink: 10,
  },
  promoColumn: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 420px',
    marginLeft: '24px',
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(3),
    },
  },
}));

export const BuyProcessFormClientInfo = () => {
  const { control, errors, onSubmit, onBack, onForward, isUpdating, id } = useClientInfo();
  const classes = useStyles();
  const isVerticalStepper = BuyProcessUtils.useIsVerticalStepper();
  const shouldDisplayPromoPrices = useMediaQuery('(min-width: 825px)');
  const { isAuthenticated } = useAuthContext();
  // This component uses RHFInputText (controlled RHF input), instead of normal
  // inputs with ref, because navigating away from the client-info step
  // will wipe the values inside the non-controlled inputs.

  return (
    <div
      data-testid={BuyProcessStepsTestIds.ClientInfo}
      className={classes.layoutContainer}
    >
      <Form onSubmit={onSubmit} className={classes.form}>
        <BuyProcessFormUserTip>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <FormSectionInfo.InfoBoxTitled
                className={classes.formIntroTitle}
                title="Beställ lagerbolag på 10 minuter!"
                titleVariant="h4"
              />

              <Typography>
                Använd vårt enkla och smidiga formulär för att beställa ditt nya
                lagerbolag. Det tar ofta mindre än 10 minuter att fylla i och
                många av stegen är förifyllda från tidigare information du
                angivit. Hjälptexter finns på varje sida och om du har frågor så
                ring oss på 08-23 01 23.
                <br />
                <strong>
                  På sidan 3 i beställningsformuläret väljer du själv om du vill
                  ha ett aktiekapital om 25.000 kr eller 50.000 kr, kostnaden är
                  densamma oavsett vilket alternativ du väljer.
                </strong>
              </Typography>
            </div>

            {isVerticalStepper && shouldDisplayPromoPrices && (
              <PromoPanelPrices
                style={{
                  minWidth: '420px',
                  marginLeft: '32px',
                }}
              />
            )}
          </div>
        </BuyProcessFormUserTip>

        <div>
          <FormSectionInfo.InfoBoxTitled title="Personlig information">
            <Typography>
              Börja med att ange kontaktperson för beställningen. Detta är den
              person som vi håller kontakt med och skickar ut handlingar till.
            </Typography>
          </FormSectionInfo.InfoBoxTitled>

          <FormSectionContainer cardClasses={classes.fieldsContainer}>
            <RHFInputText
              control={control}
              errors={errors}
              name="firstName"
              label="Förnamn"
              required
            />
            <RHFInputText
              control={control}
              errors={errors}
              name="lastName"
              label="Efternamn"
              required
            />

            <RHFInputText
              placeholder={SWEDISH_SSN_PLACEHOLDER}
              name="socialSecurityNumber"
              label="Personnummer"
              control={control}
              errors={errors}
              InputProps={{ inputComponent: MaskedSwedishSsnInput }}
              helperText="* Om du ska ha någon typ av roll i det nya bolaget"
            />
          </FormSectionContainer>
        </div>

        <div>
          <FormSectionInfo.InfoBoxTitled title="Kontaktinformation" />
          <FormSectionContainer cardClasses={classes.fieldsContainer}>
            <RHFInputText
              errors={errors}
              control={control}
              required
              name="address"
              label="Utdelningsadress"
            />
            <RHFInputText
              name="postalCode"
              defaultValue=""
              label="Postnummer"
              control={control}
              errors={errors}
              InputProps={{ inputComponent: MaskedSwedishPostNumberInput }}
              required
            />

            <RHFInputText
              errors={errors}
              control={control}
              required
              name="city"
              label="Ort"
            />

            <RHFInputText
              errors={errors}
              control={control}
              required
              name="phoneNumber"
              label="Telefon"
            />

            <RHFInputText
              errors={errors}
              control={control}
              helperText="* Om beställaren är ett företag"
              name="organizationName"
              label="Företagsnamn"
            />
          </FormSectionContainer>
        </div>

        <div>
          <FormSectionInfo.InfoBoxTitled title="E-postadress" />
          <FormSectionContainer>
            <Grid container spacing={2}>
              <Grid xs={12} item sm={6}>
                <RHFInputText
                  errors={errors}
                  control={control}
                  required
                  name="email"
                  label="E-postadress"
                  helperText="* Hit skickar vi information, instruktioner, etc."
                />
              </Grid>

              <Grid xs={12} item sm={6}>
                <RHFInputText
                  errors={errors}
                  control={control}
                  required
                  name="emailRepeat"
                  label="Bekräfta e-post"
                  helperText="* Skriv in din e-postadress igen."
                />
              </Grid>
            </Grid>
          </FormSectionContainer>
        </div>

        <ButtonGroup>
          {isAuthenticated && id !== CUSTOMER_FORM_ID && (
            <Button onClick={onBack}>Tillbaka</Button>
          )}
          <BuyProcessFormNextButton onClick={onForward}>
            Nästa
          </BuyProcessFormNextButton>
          <BuyProcessFormAdminSubmitButton loading={isUpdating}>
            Spara
          </BuyProcessFormAdminSubmitButton>
        </ButtonGroup>
      </Form>

      {!isVerticalStepper && (
        <div className={classes.promoColumn}>
          <PromoPanelPrices />

          <img
            style={{
              width: '100%',
              border: '4px solid #f4f4f4',
            }}
            src={LindaImage}
            alt="Image from a CBS staff member"
          />

          <Typography>
            "Jag hjälper dig gärna om du har frågor om lagerbolag. Ring oss på
            08-23 01 23 eller använd chattfunktionen!" - Linda
          </Typography>
        </div>
      )}
    </div>
  );
};

type PromoPanelPricesProps = {
  style?: React.CSSProperties;
};

function PromoPanelPrices(props: PromoPanelPricesProps) {
  return (
    <PromoPanelContainer {...props.style}>
      <PromoPanelTitle style={{ marginBottom: '16px' }}>
        Vårt baserbjudande är <span style={{ fontSize: '1.5em' }}>grymt!</span>
      </PromoPanelTitle>

      <PromoPanelRow Icon={<Icons.Tick />}>
        Aktiekapital 25/50 000 kr
      </PromoPanelRow>

      <PromoPanelRow Icon={<Icons.Tick />}>
        Bolagspärm i pdf-format
      </PromoPanelRow>

      <div
        style={{
          margin: '32px 0',
        }}
      >
        {/* Increased price test */}
        <Typography
          variant="h5"
          style={{
            fontFamily: 'Editor',
          }}
        >
          Total kostnad: 8 295 kr
        </Typography>
        <Typography variant="caption">(inkl. moms och avgifter)</Typography>
      </div>

      <PromoPanelTitle style={{ marginBottom: '16px' }}>
        Tilläggstjänster
      </PromoPanelTitle>

      <PromoPanelRow Icon={<Icons.Plus />}>
        Namngranskning: +475 kr
      </PromoPanelRow>
      <PromoPanelRow Icon={<Icons.Plus />}>
        Ansökan om f-skatt, moms och arbetsgivare: +695 kr
      </PromoPanelRow>
      <PromoPanelRow Icon={<Icons.Plus />}>
        Anmälan verklig huvudman +445 kr
      </PromoPanelRow>
    </PromoPanelContainer>
  );
}
